import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable } from 'rxjs';
import { environment } from '../../environments/environment';

export interface OrganizationSignupRequest {
  companyName: string;
  adminName: string;
  adminRole?: string;
  adminEmail: string;
  msalUserId: string;
}

export interface OrganizationSignupResponse {
  success: boolean;
  data?: {
    organizationId: string;
    companyName: string;
    adminName: string;
    adminEmail: string;
    adminRole: string;
    createdAt: string;
  };
  message: string;
  error?: string;
  details?: string;
}

export interface OrganizationCheckResponse {
  success: boolean;
  data: {
    organizationId: string | null;
    companyName: string | null;
    userRole?: string;
    userName?: string;
    userEmail?: string;
    status?: string;
  };
  message: string;
}

export interface OrganizationMember {
  id: string;
  name: string;
  email: string;
  role: string;
  status: string;
  joinedAt: string;
}

export interface OrganizationMembersResponse {
  success: boolean;
  data: {
    members: OrganizationMember[];
  };
  message: string;
}

// Invite Member Interfaces
export interface InviteMemberRequest {
  organizationId: string;
  invitedEmail: string;
  invitedByUserId: string;
}

export interface InviteData {
  inviteId: string;
  inviteToken: string;
  inviteLink: string;
  invitedEmail: string;
  expiresAt: string;
  emailSent: boolean;
  emailError?: string;
}

export interface InviteMemberResponse {
  success: boolean;
  data?: InviteData;
  message: string;
  error?: string;
}

// Accept Invite Interfaces
export interface AcceptInviteRequest {
  inviteToken: string;
  msalUserId: string;
  userEmail: string;
  userName: string;
}

export interface AcceptInviteData {
  organizationId: string;
  organizationName: string;
  userRole: string;
}

export interface AcceptInviteResponse {
  success: boolean;
  data?: AcceptInviteData;
  message: string;
  error?: string;
}

// Pending Invites Interfaces
export interface PendingInvite {
  inviteId: string;
  organizationId: string;
  organizationName: string;
  inviteToken: string;
  invitedByName: string;
  createdAt: string;
  expiresAt: string;
}

export interface PendingInvitesResponse {
  success: boolean;
  data: PendingInvite[];
  message: string;
}

// Organization Invites Interfaces
export interface OrganizationInvite {
  inviteId: string;
  inviteToken: string;
  invitedEmail: string;
  invitedByName: string;
  status: string;
  createdAt: string;
  expiresAt: string;
}

export interface OrganizationInvitesResponse {
  success: boolean;
  data: OrganizationInvite[];
  message: string;
}

// Remove Member & Revoke Invite Interfaces
export interface RemoveMemberResponse {
  success: boolean;
  message: string;
  error?: string;
}

export interface RevokeInviteResponse {
  success: boolean;
  message: string;
  error?: string;
}

// Organization Settings Interfaces
export interface OrganizationSettings {
  id: string;
  organizationId: string;
  autoRecordMeetings: boolean;
  recordingRetentionDays: number;
  createdAt: string;
  updatedAt: string;
  updatedBy?: string;
}

export interface OrganizationSettingsResponse {
  success: boolean;
  data?: OrganizationSettings;
  message: string;
  error?: string;
}

export interface UpdateOrganizationSettingsRequest {
  organizationId: string;
  autoRecordMeetings?: boolean;
  recordingRetentionDays?: number;
  updatedBy: string;
}

@Injectable({
  providedIn: 'root',
})
export class OrganizationService {
  private readonly apiUrl =
    environment.apis['organization-api'] || 'http://localhost:3002';

  constructor(private http: HttpClient) {}

  /**
   * Create a new organization (company signup)
   */
  createOrganization(
    request: OrganizationSignupRequest,
  ): Observable<OrganizationSignupResponse> {
    return this.http.post<OrganizationSignupResponse>(
      `${this.apiUrl}/api/organizations/signup`,
      request,
    );
  }

  /**
   * Check if user has an existing organization
   */
  checkUserOrganization(
    msalUserId: string,
  ): Observable<OrganizationCheckResponse> {
    return this.http.get<OrganizationCheckResponse>(
      `${this.apiUrl}/api/organizations/by-user/${msalUserId}`,
    );
  }

  /**
   * Get organization details by ID
   */
  getOrganizationById(organizationId: string): Observable<unknown> {
    return this.http.get(`${this.apiUrl}/api/organizations/${organizationId}`);
  }

  /**
   * Check if organization API is healthy
   */
  checkHealth(): Observable<unknown> {
    return this.http.get(`${this.apiUrl}/health`);
  }

  /**
   * Get all members of an organization
   */
  getOrganizationMembers(
    organizationId: string,
  ): Observable<OrganizationMembersResponse> {
    return this.http.get<OrganizationMembersResponse>(
      `${this.apiUrl}/api/organizations/${organizationId}/members`,
    );
  }

  /**
   * Invite a member to organization
   */
  inviteMember(request: InviteMemberRequest): Observable<InviteMemberResponse> {
    return this.http.post<InviteMemberResponse>(
      `${this.apiUrl}/api/organizations/${request.organizationId}/invites`,
      request,
    );
  }

  /**
   * Accept an organization invite
   */
  acceptInvite(
    token: string,
    request: AcceptInviteRequest,
  ): Observable<AcceptInviteResponse> {
    return this.http.post<AcceptInviteResponse>(
      `${this.apiUrl}/api/organizations/invites/${token}/accept`,
      request,
    );
  }

  /**
   * Get pending invites for a user by email
   */
  getPendingInvites(email: string): Observable<PendingInvitesResponse> {
    return this.http.get<PendingInvitesResponse>(
      `${this.apiUrl}/api/organizations/invites/pending?email=${encodeURIComponent(email)}`,
    );
  }

  /**
   * Get all invites for an organization (admin only)
   */
  getOrganizationInvites(
    organizationId: string,
  ): Observable<OrganizationInvitesResponse> {
    return this.http.get<OrganizationInvitesResponse>(
      `${this.apiUrl}/api/organizations/${organizationId}/invites`,
    );
  }

  /**
   * Revoke an organization invite (admin only)
   */
  revokeInvite(
    organizationId: string,
    inviteId: string,
    requestingUserId: string,
  ): Observable<RevokeInviteResponse> {
    return this.http.delete<RevokeInviteResponse>(
      `${this.apiUrl}/api/organizations/${organizationId}/invites/${inviteId}?requestingUserId=${encodeURIComponent(requestingUserId)}`,
    );
  }

  /**
   * Remove a member from organization (admin only)
   */
  removeMember(
    organizationId: string,
    memberUserId: string,
    requestingUserId: string,
  ): Observable<RemoveMemberResponse> {
    return this.http.delete<RemoveMemberResponse>(
      `${this.apiUrl}/api/organizations/${organizationId}/members/${memberUserId}?requestingUserId=${encodeURIComponent(requestingUserId)}`,
    );
  }

  /**
   * Get organization settings
   */
  getOrganizationSettings(
    organizationId: string,
  ): Observable<OrganizationSettingsResponse> {
    return this.http.get<OrganizationSettingsResponse>(
      `${this.apiUrl}/api/organizations/${organizationId}/settings`,
    );
  }

  /**
   * Update organization settings (admin only)
   */
  updateOrganizationSettings(
    organizationId: string,
    request: UpdateOrganizationSettingsRequest,
  ): Observable<OrganizationSettingsResponse> {
    return this.http.put<OrganizationSettingsResponse>(
      `${this.apiUrl}/api/organizations/${organizationId}/settings`,
      request,
    );
  }
}
